const FOOTER_SIGNUP_FORM_SELECTOR = "[data-footer-signup-form]";
const SIGNUP_PREFILL_FORM_SELECTOR = "[data-signup-prefill-form]";
const SIGNUP_PREFILL_INPUT_SELECTOR = 'input[type="email"], input[name*="email"], input[id*="email"]';
const SIGNUP_EMAIL_PARAM_NAME = "email";
const SIGNUP_EMAIL_MAX_LENGTH = 254;
const SIGNUP_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmailValue = (value: string): string => value.trim().slice(0, SIGNUP_EMAIL_MAX_LENGTH);

const getEmailFromParams = (value: string): string => {
	const params = new URLSearchParams(value.replace(/^[#?]/, ""));
	const email = normalizeEmailValue(params.get(SIGNUP_EMAIL_PARAM_NAME) ?? "");

	if (email && SIGNUP_EMAIL_PATTERN.test(email)) {
		return email;
	}

	for (const [key, paramValue] of params.entries()) {
		const fallbackEmail = normalizeEmailValue(paramValue);

		if (key.toLowerCase().includes(SIGNUP_EMAIL_PARAM_NAME) && SIGNUP_EMAIL_PATTERN.test(fallbackEmail)) {
			return fallbackEmail;
		}
	}

	return "";
};

const getSignupEmail = (): string => {
	return getEmailFromParams(window.location.hash) || getEmailFromParams(window.location.search);
};

const removeEmailParams = (params: URLSearchParams): boolean => {
	let didRemove = false;

	for (const key of Array.from(params.keys())) {
		if (key.toLowerCase().includes(SIGNUP_EMAIL_PARAM_NAME)) {
			params.delete(key);
			didRemove = true;
		}
	}

	return didRemove;
};

const clearSignupEmailHandoff = (): void => {
	const queryParams = new URLSearchParams(window.location.search);
	const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
	const didRemoveQueryEmail = removeEmailParams(queryParams);
	const didRemoveHashEmail = removeEmailParams(hashParams);

	if (!didRemoveQueryEmail && !didRemoveHashEmail) {
		return;
	}

	const queryString = queryParams.toString();
	const hashString = didRemoveHashEmail ? hashParams.toString() : window.location.hash.replace(/^#/, "");
	const nextQuery = queryString ? `?${queryString}` : "";
	const nextHash = hashString ? `#${hashString}` : "";

	window.history.replaceState(window.history.state, document.title, `${window.location.pathname}${nextQuery}${nextHash}`);
};

const prefillSignupInput = (container: ParentNode, email: string): boolean => {
	const input = container.querySelector<HTMLInputElement>(SIGNUP_PREFILL_INPUT_SELECTOR);

	if (!input) {
		return false;
	}

	if (input.value) {
		return true;
	}

	input.value = email;
	input.dispatchEvent(new Event("input", { bubbles: true }));
	input.dispatchEvent(new Event("change", { bubbles: true }));

	return true;
};

const prefillSignupForms = (email: string): boolean => {
	const forms = document.querySelectorAll<HTMLElement>(SIGNUP_PREFILL_FORM_SELECTOR);
	const filledForms: HTMLElement[] = [];

	for (const form of forms) {
		if (prefillSignupInput(form, email)) {
			filledForms.push(form);
		}
	}

	return filledForms.length > 0;
};

const initSignupPrefill = (): void => {
	const email = getSignupEmail();

	if (!email) {
		return;
	}

	clearSignupEmailHandoff();

	if (prefillSignupForms(email)) {
		clearSignupEmailHandoff();
		return;
	}

	const observer = new MutationObserver(() => {
		if (prefillSignupForms(email)) {
			clearSignupEmailHandoff();
			observer.disconnect();
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	window.setTimeout(() => {
		observer.disconnect();
	}, 10000);
};

const initFooterSignupRedirect = (): void => {
	const forms = document.querySelectorAll<HTMLFormElement>(FOOTER_SIGNUP_FORM_SELECTOR);

	for (const form of forms) {
		form.addEventListener("submit", (event) => {
			const input = form.querySelector<HTMLInputElement>(SIGNUP_PREFILL_INPUT_SELECTOR);

			if (!input) {
				return;
			}

			const email = normalizeEmailValue(input.value);
			input.value = email;

			if (!input.checkValidity()) {
				event.preventDefault();
				input.reportValidity();
				return;
			}

			event.preventDefault();

			const actionUrl = new URL(form.action, window.location.href);
			if (actionUrl.origin !== window.location.origin) {
				return;
			}

			const params = new URLSearchParams();
			params.set(SIGNUP_EMAIL_PARAM_NAME, email);

			actionUrl.hash = params.toString();
			window.location.assign(actionUrl.toString());
		});
	}
};

const initSignupEmailHandoff = (): void => {
	initFooterSignupRedirect();
	initSignupPrefill();
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initSignupEmailHandoff);
} else {
	initSignupEmailHandoff();
}
